/**
 * The decision controls (docs/ux/casefile.md, Forms): one radio group of
 * Accept, Change and Escalate with category-specific labels; the change's
 * fields always visible under Change, so the choices are read without a
 * click, and touching one selects Change ("Correct" beside "Accept" read as
 * the adjective, and the merge option was hidden until it was chosen: the
 * first real review, D-0080); a note (folded away unless escalating
 * or already written, so Save stays above the fold); one Save button
 * carrying the verb. A merge and a duplicate mark confirm with their
 * consequence; accept does not. Locked, the column is the sentence naming
 * who completed the review and when.
 */

import { ChevronRightIcon } from 'lucide-react';
import { useEffect, useId, useMemo, useState, type FormEvent } from 'react';
import type { CasefileDocument, CasefileManifest, DecisionInput, SortCorrection, SortReviewItem } from '@/app/types';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { longDate } from '@/lib/format';
import { GROUPED_SECTIONS, MONTH_NAMES, SECTIONS, acceptLabel, billableAfterDuplicate, copyOf, daysInMonth, docLine, isEscalationWorthy, pageCount, pagesLower, pagesText, previewTitle, previousDoc } from './describe';
import { escalationNote } from './ReviewQueue';

type Choice = 'accepted' | 'corrected' | 'escalated';
type Field = 'section' | 'date' | 'provider';
type AdditionalKind = 'reclassify' | 'set_date' | 'set_provider' | 'duplicate_of' | 'merge' | 'split';

export type Draft = {
  choice: Choice | null;
  boundary: 'merge' | 'move';
  toPage: string;
  section: string;
  day: string;
  month: string;
  year: string;
  provider: string;
  ocrField: Field | null;
  additionalKind: AdditionalKind | null;
  duplicateOf: string;
  note: string;
};

function draftFrom(item: SortReviewItem | null, doc: CasefileDocument | undefined, hasPrevious: boolean): Draft {
  const c = item?.correction;
  return {
    choice: item && item.decision !== 'unresolved' ? item.decision : null,
    // Merge is the usual answer to a doubtful boundary; the first document has nothing before it.
    boundary: c?.kind === 'move_boundary' || !hasPrevious ? 'move' : 'merge',
    toPage: c?.kind === 'move_boundary' ? String(c.toPage) : c?.kind === 'split' ? String(c.atPage) : '',
    section: c?.kind === 'reclassify' ? c.section : c?.kind === 'not_duplicate' ? (c.section ?? '') : '',
    day: c?.kind === 'set_date' && c.date.day ? String(c.date.day) : '',
    month: c?.kind === 'set_date' ? String(c.date.month) : '',
    year: c?.kind === 'set_date' ? String(c.date.year) : doc?.date ? String(doc.date.year) : '',
    provider: c?.kind === 'set_provider' ? c.provider : '',
    ocrField: c?.kind === 'reclassify' || c?.kind === 'not_duplicate' ? 'section' : c?.kind === 'set_date' ? 'date' : c?.kind === 'set_provider' ? 'provider' : null,
    additionalKind: c?.kind === 'reclassify' ? 'reclassify' : c?.kind === 'set_date' ? 'set_date' : c?.kind === 'set_provider' ? 'set_provider' : c?.kind === 'duplicate_of' ? 'duplicate_of' : c?.kind === 'merge' ? 'merge' : c?.kind === 'split' ? 'split' : null,
    duplicateOf: c?.kind === 'duplicate_of' ? c.ofDocumentId : '',
    note: item?.note ?? '',
  };
}

export type ControlsProps = {
  /** The flagged or additional item being decided, or null for a document picked for a new additional correction. */
  item: SortReviewItem | null;
  document: CasefileDocument | undefined;
  manifest: CasefileManifest;
  escalationConfidence: number;
  locked: { by: string; at: string } | null;
  offline: boolean;
  busy: boolean;
  /** The last save failed (the toast has said so); the status line says it too. */
  saveFailed: boolean;
  saved: { at: string; by: string } | null;
  onSubmit: (input: DecisionInput) => void;
  onWithdraw?: () => void;
  onBoundaryPreview?: (page: number | null) => void;
};

function FieldError({ id, text }: { id: string; text: string | null }) {
  return text ? <p id={id} role="alert" className="text-[13px] text-beam-error">{text}</p> : null;
}

/** Draft keys that belong to a change: touching one is choosing Change. */
const CHANGE_KEYS: ReadonlySet<keyof Draft> = new Set<keyof Draft>(['boundary', 'toPage', 'section', 'day', 'month', 'year', 'provider', 'ocrField', 'duplicateOf']);

/** Which control an error key belongs to, for focus and aria-describedby. */
const ERROR_TARGET: Record<string, string> = {
  choice: 'accept', note: 'note', section: 'section', date: 'day', provider: 'provider', toPage: 'page', boundary: 'move', additionalKind: 'kind', duplicateOf: 'dup', ocrField: 'what',
};

export function DecisionControls(props: ControlsProps) {
  const { item, document: doc, manifest, escalationConfidence, locked, offline, busy, saveFailed, saved, onSubmit, onWithdraw, onBoundaryPreview } = props;
  const additionalMode = item === null || item.category === 'additional';
  const previous = doc ? previousDoc(manifest, doc) : undefined;
  const [draft, setDraft] = useState<Draft>(() => draftFrom(item, doc, previous !== undefined));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [noteOpen, setNoteOpen] = useState(() => Boolean(item?.note));
  const [confirm, setConfirm] = useState<{ kind: 'merge' | 'duplicate'; input: DecisionInput; title: string; body: string; action: string; cancel: string } | null>(null);
  const [dirty, setDirty] = useState(false);
  const id = useId();
  useEffect(() => { setDraft(draftFrom(item, doc, previous !== undefined)); setErrors({}); setDirty(false); setNoteOpen(Boolean(item?.note)); }, [item?.itemId, item?.at, doc?.id, previous === undefined]);

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) => { setDraft((d) => ({ ...d, [key]: value, ...(CHANGE_KEYS.has(key) && !additionalMode ? { choice: 'corrected' as Choice } : {}) })); setDirty(true); };
  const described = (key: string): string | undefined => (errors[key] ? `${id}-${key}-error` : undefined);

  useEffect(() => {
    if (!onBoundaryPreview) return;
    const page = draft.choice === 'corrected' && draft.boundary === 'move' ? Number(draft.toPage) : NaN;
    onBoundaryPreview(Number.isInteger(page) && page > 0 ? page : null);
  }, [draft.choice, draft.boundary, draft.toPage, onBoundaryPreview]);

  const category = item?.category ?? 'additional';
  const activeField: Field | null = category === 'low-confidence-section' ? 'section' : category === 'undated-document' ? 'date' : category === 'unattributed-provider' ? 'provider' : category === 'low-confidence-ocr' ? draft.ocrField : additionalMode ? (draft.additionalKind === 'reclassify' ? 'section' : draft.additionalKind === 'set_date' ? 'date' : draft.additionalKind === 'set_provider' ? 'provider' : null) : null;

  const datePreview = useMemo(() => {
    if (!doc || activeField !== 'date' || !draft.month || !draft.year) return null;
    const date = { year: Number(draft.year), month: Number(draft.month), day: draft.day ? Number(draft.day) : null };
    return previewTitle(doc, { date });
  }, [doc, activeField, draft.day, draft.month, draft.year]);

  if (locked) {
    return (
      <div data-slot="locked-line" className="rounded-xl border border-beam-line bg-beam-paper p-4 text-[13px]">
        Decisions are locked: the review was completed by {locked.by} on {longDate(locked.at)} at {new Date(locked.at).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: false })}.
      </div>
    );
  }
  if (!doc) return <div className="rounded-xl border border-beam-line bg-beam-paper p-4 text-[13px] text-beam-text-muted">No document covers this page; escalate it.</div>;

  const inert = saved !== null && !dirty;

  function correction(): { correction?: SortCorrection; error?: [string, string] } {
    const documentId = doc!.id;
    // A section chosen for a copy says it is not a copy; the server refuses a plain reclassify there.
    const kind = additionalMode ? draft.additionalKind : category === 'low-confidence-boundary' ? (draft.boundary === 'merge' ? 'merge' : 'move_boundary') : activeField === 'section' ? (doc!.duplicateOf ? 'not_duplicate' : 'reclassify') : activeField === 'date' ? 'set_date' : activeField === 'provider' ? 'set_provider' : category === 'ambiguous-near-duplicate' ? 'duplicate_of' : null;
    if (!kind) return { error: additionalMode ? ['additionalKind', 'Choose a change'] : ['ocrField', 'Choose what to correct'] };
    switch (kind) {
      case 'merge':
        if (!previous) return { error: ['boundary', 'This is the first document in the bundle'] };
        return { correction: { kind: 'merge', documentId, intoDocumentId: previous.id } };
      case 'move_boundary': {
        const min = previous ? previous.startPage + 1 : doc!.startPage + 1;
        const page = Number(draft.toPage);
        if (!Number.isInteger(page) || page < min || page > doc!.endPage || page === doc!.startPage) return { error: ['toPage', `Choose a page between ${min} and ${doc!.endPage}, other than ${doc!.startPage}`] };
        return { correction: { kind: 'move_boundary', documentId, toPage: page } };
      }
      case 'reclassify':
        if (!draft.section) return { error: ['section', 'Choose a section'] };
        return { correction: { kind: 'reclassify', documentId, section: draft.section } };
      case 'split': {
        const page = Number(draft.toPage);
        if (!Number.isInteger(page) || page <= doc!.startPage || page > doc!.endPage) return { error: ['toPage', `Choose a page between ${doc!.startPage + 1} and ${doc!.endPage}`] };
        return { correction: { kind: 'split', documentId, atPage: page } };
      }
      case 'not_duplicate':
        if (!draft.section) return { error: ['section', 'Choose a section'] };
        return { correction: { kind: 'not_duplicate', documentId, section: draft.section } };
      case 'set_date': {
        const month = Number(draft.month); const year = Number(draft.year);
        const currentYear = new Date().getFullYear();
        if (!month || !year) return { error: ['date', 'Choose a month and a year'] };
        if (!Number.isInteger(year) || year < 1980 || year > currentYear) return { error: ['date', `Enter a year between 1980 and ${currentYear}`] };
        const day = draft.day ? Number(draft.day) : null;
        if (day !== null && (!Number.isInteger(day) || day < 1 || day > daysInMonth(year, month))) return { error: ['date', `${MONTH_NAMES[month - 1]} has ${daysInMonth(year, month)} days`] };
        return { correction: { kind: 'set_date', documentId, date: { year, month, day } } };
      }
      case 'set_provider': {
        const provider = draft.provider.trim();
        if (!provider) return { error: ['provider', 'Enter the practitioner or practice'] };
        if (provider.length > 80) return { error: ['provider', 'Use at most 80 characters'] };
        return { correction: { kind: 'set_provider', documentId, provider } };
      }
      case 'duplicate_of': {
        if (additionalMode) {
          if (!draft.duplicateOf) return { error: ['duplicateOf', 'Choose the earlier document'] };
          return { correction: { kind: 'duplicate_of', documentId, ofDocumentId: draft.duplicateOf } };
        }
        const [earlier, later] = [item!.documentIds[0], item!.documentIds[1]];
        if (!earlier || !later) return { error: ['duplicate', 'The machine did not name both documents'] };
        return { correction: { kind: 'duplicate_of', documentId: later, ofDocumentId: earlier } };
      }
      default:
        return { error: ['additionalKind', 'Choose a change'] };
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    if (busy || offline || inert) return;
    const next: Record<string, string> = {};
    if (!draft.choice && !additionalMode) next.choice = 'Choose Accept, Change or Escalate.';
    const choice: Choice | null = additionalMode ? 'corrected' : draft.choice;
    const note = draft.note.trim();
    if (choice === 'escalated' && !note) next.note = 'Say what needs another look';
    let input: DecisionInput | null = null;
    if (choice === 'corrected') {
      const c = correction();
      if (c.error) next[c.error[0]] = c.error[1];
      else input = { decision: 'corrected', correction: c.correction!, ...(note ? { note } : {}) };
    } else if (choice) {
      input = { decision: choice, ...(note ? { note } : {}) };
    }
    setErrors(next);
    const first = Object.keys(next)[0];
    if (first) {
      if (first === 'note') setNoteOpen(true);
      requestAnimationFrame(() => document.getElementById(`${id}-${ERROR_TARGET[first] ?? first}`)?.focus());
      return;
    }
    if (!input) return;
    if (input.correction?.kind === 'merge' && previous) {
      const same = previous.title === doc!.title;
      setConfirm({
        kind: 'merge', input,
        title: 'Merge with the document before?',
        body: `Pages ${previous.startPage} to ${doc!.endPage} become one document, titled as the earlier one: ${previous.title}. ${same ? 'Both carry that title; one entry leaves the schedule of documents.' : `The later title (${doc!.title}) leaves the schedule of documents.`} This applies to the reviewed casefile when the review is completed. The machine result is kept as it is. You can re-decide this item until then.`,
        action: 'Merge documents', cancel: 'Keep separate',
      });
      return;
    }
    if (input.correction?.kind === 'duplicate_of') {
      const later = manifest.documents.find((d) => d.id === input!.correction!.documentId)!;
      const earlier = manifest.documents.find((d) => d.id === (input!.correction as { ofDocumentId: string }).ofDocumentId);
      setConfirm({
        kind: 'duplicate', input,
        title: 'Mark the later document a duplicate?',
        body: `${docLine(later)}, ${pageCount(later)} ${pageCount(later) === 1 ? 'page' : 'pages'}, moves to Duplicates as a copy of ${earlier ? pagesText(earlier.startPage, earlier.endPage).toLowerCase() : 'the earlier document'}. Billable pages: ${manifest.billablePages} becomes ${billableAfterDuplicate(manifest, later.id)} when the review is completed. The machine result is kept as it is. You can re-decide this item until then.`,
        action: 'Mark duplicate', cancel: 'Keep both',
      });
      return;
    }
    onSubmit(input);
  }

  const saveLabel = busy ? (draft.choice === 'escalated' ? 'Escalating' : 'Saving') : inert ? 'Save changes' : additionalMode ? 'Save change' : draft.choice === 'accepted' ? 'Save: accept' : draft.choice === 'escalated' ? 'Escalate item' : draft.choice === 'corrected' ? 'Save change' : 'Save';
  const providers = doc ? [...new Set(manifest.documents.filter((d) => d.section === doc.section && d.provider).map((d) => d.provider!))] : [];
  const worthy = item ? isEscalationWorthy(item, escalationConfidence) : false;
  const sectionOptions = SECTIONS.filter((s) => s !== 'Duplicates' && s !== doc.section);
  const copied = copyOf(manifest, doc);
  const noteRequired = draft.choice === 'escalated';
  const showNote = noteOpen || noteRequired || draft.note.length > 0;
  const statusText = offline ? 'Actions are paused while offline' : saveFailed && dirty ? 'Not saved: try again.' : saved ? `Saved ${new Date(saved.at).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit', hour12: false })} by ${saved.by}` : 'Not saved yet';

  const fields = (
    <>
      {activeField === 'section' ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${id}-section`}>Section</Label>
          {copied ? <p className="text-xs text-beam-text-muted">The machine filed these {pageCount(doc)} pages as a copy of {pagesLower(copied.startPage, copied.endPage)}. Choosing a section says they are not a copy and makes them billable again: {manifest.billablePages} becomes {manifest.billablePages + pageCount(doc)} when the review is completed.</p> : null}
          <Select value={draft.section} onValueChange={(v) => set('section', v)}>
            <SelectTrigger id={`${id}-section`} className="h-9" aria-invalid={errors.section ? true : undefined} aria-describedby={described('section')}><SelectValue placeholder="Choose a section" /></SelectTrigger>
            <SelectContent>{sectionOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
          </Select>
          {draft.section && GROUPED_SECTIONS.has(draft.section) && !doc.provider ? <p className="text-xs text-beam-text-muted">{draft.section} groups by provider. This document has none and will sit under Unattributed; set the provider with an additional correction after saving.</p> : null}
          <FieldError id={`${id}-section-error`} text={errors.section ?? null} />
        </div>
      ) : null}
      {activeField === 'date' ? (
        <div className="flex flex-col gap-1">
          <div className="grid grid-cols-[64px_minmax(0,1fr)_80px] gap-2">
            <div className="flex flex-col gap-1"><Label htmlFor={`${id}-day`}>Day</Label><Input id={`${id}-day`} inputMode="numeric" value={draft.day} onChange={(e) => set('day', e.target.value)} className="h-9" placeholder="Optional" aria-invalid={errors.date ? true : undefined} aria-describedby={described('date')} /></div>
            <div className="flex flex-col gap-1">
              <Label htmlFor={`${id}-month`}>Month</Label>
              <Select value={draft.month} onValueChange={(v) => set('month', v)}>
                <SelectTrigger id={`${id}-month`} className="h-9" aria-invalid={errors.date ? true : undefined} aria-describedby={described('date')}><SelectValue placeholder="Month" /></SelectTrigger>
                <SelectContent>{MONTH_NAMES.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1"><Label htmlFor={`${id}-year`}>Year</Label><Input id={`${id}-year`} inputMode="numeric" value={draft.year} onChange={(e) => set('year', e.target.value)} className="h-9" aria-invalid={errors.date ? true : undefined} aria-describedby={described('date')} /></div>
          </div>
          {datePreview ? <p className="text-xs text-beam-text-muted">Title becomes: {datePreview}</p> : null}
          <FieldError id={`${id}-date-error`} text={errors.date ?? null} />
        </div>
      ) : null}
      {activeField === 'provider' ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${id}-provider`}>Practitioner or practice</Label>
          <Input id={`${id}-provider`} value={draft.provider} maxLength={80} onChange={(e) => set('provider', e.target.value)} className="h-9" aria-invalid={errors.provider ? true : undefined} aria-describedby={described('provider')} />
          {providers.length > 0 ? (
            <div className="flex flex-col gap-1">
              <span className="text-xs text-beam-text-muted">Already in {doc.section}:</span>
              <div className="flex flex-wrap gap-1.5">{providers.map((p) => <Button key={p} type="button" variant="outline" size="sm" className="h-8" onClick={() => set('provider', p)}>{p}</Button>)}</div>
            </div>
          ) : null}
          {draft.provider.trim() ? <p className="text-xs text-beam-text-muted">Title becomes: {previewTitle(doc, { provider: draft.provider.trim() })}; filed under {draft.provider.trim()}.</p> : null}
          <FieldError id={`${id}-provider-error`} text={errors.provider ?? null} />
        </div>
      ) : null}
    </>
  );

  return (
    <form aria-labelledby={`${id}-legend`} data-slot="decision-controls" noValidate onSubmit={submit} className="flex flex-col gap-4 rounded-xl border border-beam-line bg-beam-paper p-4">
      <h3 id={`${id}-legend`} className="eyebrow text-beam-text-muted">Your decision</h3>
      {additionalMode ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${id}-kind`}>Change</Label>
          <Select value={draft.additionalKind ?? ''} onValueChange={(v) => set('additionalKind', v as AdditionalKind)}>
            <SelectTrigger id={`${id}-kind`} className="h-9" aria-invalid={errors.additionalKind ? true : undefined} aria-describedby={described('additionalKind')}><SelectValue placeholder="Choose a change" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="reclassify">Reclassify</SelectItem>
              <SelectItem value="set_date">Set the date</SelectItem>
              <SelectItem value="set_provider">Set the provider</SelectItem>
              <SelectItem value="duplicate_of">Mark as a duplicate of another document</SelectItem>
              <SelectItem value="merge" disabled={!previous}>{previous ? 'Merge with the document before' : 'Merge with the document before (this is the first document in the bundle)'}</SelectItem>
              <SelectItem value="split" disabled={pageCount(doc) < 2}>{pageCount(doc) < 2 ? 'Split at a page (this document has one page)' : 'Split at a page'}</SelectItem>
            </SelectContent>
          </Select>
          <FieldError id={`${id}-additionalKind-error`} text={errors.additionalKind ?? null} />
          {draft.additionalKind === 'duplicate_of' ? (
            <div className="mt-2 flex flex-col gap-1">
              <Label htmlFor={`${id}-dup`}>Duplicate of</Label>
              <Select value={draft.duplicateOf} onValueChange={(v) => set('duplicateOf', v)}>
                <SelectTrigger id={`${id}-dup`} className="h-9" aria-invalid={errors.duplicateOf ? true : undefined} aria-describedby={described('duplicateOf')}><SelectValue placeholder="Choose the earlier document" /></SelectTrigger>
                <SelectContent>{manifest.documents.filter((d) => d.id !== doc.id && !d.duplicateOf && d.section !== 'Duplicates' && d.startPage < doc.startPage).map((d) => <SelectItem key={d.id} value={d.id}>{docLine(d)}</SelectItem>)}</SelectContent>
              </Select>
              {draft.duplicateOf ? <p className="text-xs text-beam-text-muted">Billable pages: {manifest.billablePages} becomes {billableAfterDuplicate(manifest, doc.id)}.</p> : null}
              <FieldError id={`${id}-duplicateOf-error`} text={errors.duplicateOf ?? null} />
            </div>
          ) : null}
          {draft.additionalKind === 'merge' && previous ? <p className="text-xs text-beam-text-muted">Pages {previous.startPage} to {doc.endPage} become one document, titled as the earlier one.</p> : null}
          {draft.additionalKind === 'split' ? (() => {
            const page = Number(draft.toPage);
            const valid = Number.isInteger(page) && page > doc.startPage && page <= doc.endPage;
            return (
              <div className="mt-2 flex flex-col gap-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Label htmlFor={`${id}-page`} className="text-xs">First page of the new document</Label>
                  <Input id={`${id}-page`} inputMode="numeric" value={draft.toPage} onChange={(e) => set('toPage', e.target.value)} className="h-9 w-20" aria-invalid={errors.toPage ? true : undefined} aria-describedby={errors.toPage ? `${id}-toPage-error ${id}-split-hint` : `${id}-split-hint`} />
                  <span id={`${id}-split-hint`} className="text-xs text-beam-text-muted">Between {doc.startPage + 1} and {doc.endPage}</span>
                </div>
                {valid ? <p className="text-xs text-beam-text-muted">Pages {doc.startPage} to {page - 1} keep this title. Pages {page} to {doc.endPage} become a new document with the same section and title; give it its own section, date or provider afterwards through Change another document.</p> : null}
                <FieldError id={`${id}-toPage-error`} text={errors.toPage ?? null} />
              </div>
            );
          })() : null}
          <div className="mt-2 flex flex-col gap-4">{fields}</div>
        </div>
      ) : (
        <RadioGroup value={draft.choice ?? ''} onValueChange={(v) => set('choice', v as Choice)} aria-label="Your decision" aria-invalid={errors.choice ? true : undefined} aria-describedby={described('choice')} className="gap-1">
          <label htmlFor={`${id}-accept`} className="flex min-h-11 cursor-pointer items-start gap-3 rounded-lg px-2 py-2 hover:bg-beam-cream-deep">
            <RadioGroupItem id={`${id}-accept`} value="accepted" className="mt-0.5" />
            <span className="text-sm font-medium">{acceptLabel(item!, manifest)}</span>
          </label>
          <label htmlFor={`${id}-correct`} className="flex min-h-11 cursor-pointer items-start gap-3 rounded-lg px-2 py-2 hover:bg-beam-cream-deep">
            <RadioGroupItem id={`${id}-correct`} value="corrected" className="mt-0.5" />
            <span className="text-sm font-medium">Change</span>
          </label>
            <div className="ml-9 flex flex-col gap-4 pb-2">
              {category === 'low-confidence-boundary' ? (
                <RadioGroup value={draft.choice === 'corrected' ? draft.boundary : ''} onValueChange={(v) => set('boundary', v as 'merge' | 'move')} aria-label="Change" aria-describedby={described('toPage') ?? described('boundary')} className="gap-1">
                  <label htmlFor={`${id}-merge`} className="flex cursor-pointer items-start gap-3 rounded-lg px-2 py-1.5 hover:bg-beam-cream-deep">
                    <RadioGroupItem id={`${id}-merge`} value="merge" className="mt-0.5" disabled={!previous} />
                    <span className="flex flex-col"><span className="text-sm">Merge with the document before</span><span className="text-xs text-beam-text-muted">{previous ? `Pages ${previous.startPage} to ${doc.endPage} become one document, titled as the earlier one.` : 'This is the first document in the bundle.'}</span></span>
                  </label>
                  <label htmlFor={`${id}-move`} className="flex cursor-pointer items-start gap-3 rounded-lg px-2 py-1.5 hover:bg-beam-cream-deep">
                    <RadioGroupItem id={`${id}-move`} value="move" className="mt-0.5" />
                    <span className="flex flex-col gap-1">
                      <span className="text-sm">The document starts at a different page</span>
                      {draft.choice === 'corrected' && draft.boundary === 'move' ? (
                        <span className="flex items-center gap-2">
                          <Label htmlFor={`${id}-page`} className="text-xs">Page</Label>
                          <Input id={`${id}-page`} inputMode="numeric" value={draft.toPage} onChange={(e) => set('toPage', e.target.value)} className="h-9 w-20" aria-invalid={errors.toPage ? true : undefined} aria-describedby={errors.toPage ? `${id}-toPage-error ${id}-page-hint` : `${id}-page-hint`} />
                          <span id={`${id}-page-hint`} className="text-xs text-beam-text-muted">Between {previous ? previous.startPage + 1 : doc.startPage + 1} and {doc.endPage}</span>
                        </span>
                      ) : null}
                    </span>
                  </label>
                  <FieldError id={`${id}-toPage-error`} text={errors.toPage ?? null} />
                  <FieldError id={`${id}-boundary-error`} text={errors.boundary ?? null} />
                </RadioGroup>
              ) : null}
              {category === 'ambiguous-near-duplicate' ? (() => {
                const earlier = manifest.documents.find((d) => d.id === item!.documentIds[0]);
                const later = manifest.documents.find((d) => d.id === item!.documentIds[1]);
                return later && earlier ? (
                  <div className="flex flex-col gap-1 text-[13px]">
                    <p>Page {item!.endPage} is a re-scan of page {item!.startPage}: the later document ({docLine(later)}, {pageCount(later)} {pageCount(later) === 1 ? 'page' : 'pages'}) becomes a duplicate of the earlier ({pagesText(earlier.startPage, earlier.endPage).toLowerCase()}).</p>
                    <p className="text-beam-text-muted">Billable pages: {manifest.billablePages} becomes {billableAfterDuplicate(manifest, later.id)}.</p>
                    {pageCount(later) > pageCount(earlier) ? <p className="text-beam-warning">The later document has {pageCount(later)} pages and the earlier has {pageCount(earlier)}. The manifest records duplicates by document, so all {pageCount(later)} pages would move to Duplicates. If only one page is a copy, escalate this item.</p> : null}
                    <FieldError id={`${id}-duplicate-error`} text={errors.duplicate ?? null} />
                  </div>
                ) : <FieldError id={`${id}-duplicate-error`} text="The machine did not name both documents; escalate this item." />;
              })() : null}
              {category === 'low-confidence-ocr' ? (
                <div className="flex flex-col gap-1">
                  <Label htmlFor={`${id}-what`}>What to correct</Label>
                  <Select value={draft.ocrField ?? ''} onValueChange={(v) => set('ocrField', v as Field)}>
                    <SelectTrigger id={`${id}-what`} className="h-9" aria-invalid={errors.ocrField ? true : undefined} aria-describedby={described('ocrField')}><SelectValue placeholder="Choose what to correct" /></SelectTrigger>
                    <SelectContent><SelectItem value="section">Section</SelectItem><SelectItem value="date">Date</SelectItem><SelectItem value="provider">Provider</SelectItem></SelectContent>
                  </Select>
                  <FieldError id={`${id}-ocrField-error`} text={errors.ocrField ?? null} />
                </div>
              ) : null}
              {fields}
            </div>
          <label htmlFor={`${id}-escalate`} className="flex min-h-11 cursor-pointer items-start gap-3 rounded-lg px-2 py-2 hover:bg-beam-cream-deep">
            <RadioGroupItem id={`${id}-escalate`} value="escalated" className="mt-0.5" />
            <span className="flex flex-col">
              <span className="text-sm font-medium">Escalate for another look</span>
              <span className="text-xs text-beam-text-muted">Blocks completion and the 3 to 4 gate until someone re-decides this item.{worthy ? ` ${escalationNote(item!, escalationConfidence)}` : ''}</span>
            </span>
          </label>
          <FieldError id={`${id}-choice-error`} text={errors.choice ?? null} />
        </RadioGroup>
      )}

      {showNote ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${id}-note`}>{noteRequired ? 'Note (required to escalate)' : 'Note (optional)'}</Label>
          <Textarea id={`${id}-note`} rows={3} maxLength={500} value={draft.note} onChange={(e) => set('note', e.target.value)} aria-invalid={errors.note ? true : undefined} aria-describedby={described('note')} onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') (e.currentTarget.form as HTMLFormElement | null)?.requestSubmit(); }} />
          {draft.note.length >= 400 ? <span className="tabular text-xs text-beam-text-muted">{draft.note.length} of 500</span> : null}
          <FieldError id={`${id}-note-error`} text={errors.note ?? null} />
        </div>
      ) : (
        <Collapsible open={false} onOpenChange={() => setNoteOpen(true)}>
          <CollapsibleTrigger className="group flex min-h-8 items-center gap-1 text-[13px] text-beam-text-muted hover:text-foreground">
            <ChevronRightIcon aria-hidden="true" className="size-4" /> Add a note
          </CollapsibleTrigger>
          <CollapsibleContent />
        </Collapsible>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="submit"
          size="lg"
          variant={inert ? 'outline' : 'default'}
          className={inert ? 'h-9 cursor-default text-beam-text-muted hover:bg-beam-paper' : 'h-9'}
          disabled={offline}
          aria-disabled={busy || inert ? 'true' : undefined}
          aria-describedby={`${id}-status`}
        >
          {saveLabel}
        </Button>
        {onWithdraw && item?.category === 'additional' && item.decision === 'corrected' ? <Button type="button" variant="outline" size="default" className="h-8" disabled={offline} aria-disabled={busy ? 'true' : undefined} aria-label={`Withdraw the correction on ${pagesText(item.startPage, item.endPage).toLowerCase()}`} onClick={() => { if (!busy) onWithdraw(); }}>Withdraw correction</Button> : null}
        <span id={`${id}-status`} className="text-xs text-beam-text-muted">{statusText}</span>
      </div>

      <AlertDialog open={confirm !== null} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <AlertDialogContent className="sm:max-w-[560px]">
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.body}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel autoFocus>{confirm?.cancel}</AlertDialogCancel>
            <AlertDialogAction onClick={() => { if (confirm) onSubmit(confirm.input); setConfirm(null); }}>{confirm?.action}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </form>
  );
}
